// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Five routes for three jobs, and the split is by who is doing them: the office
// keeps the catalogue, the warehouse floor scans and moves, the goods-in desk
// reads delivery notes. Each has its own permission, so the sidebar a fitter
// sees and the one a buyer sees are different sidebars.

export const routes = [
  // ── The catalogue ──────────────────────────────────────────────────────────
  {
    path: '/materials',
    name: 'materials',
    component: () => import('./pages/MaterialsPage.vue'),
    meta: { permission: 'inventory:read', title: 'inventory.materials.title' },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but new sits first anyway, for the reader.
  {
    path: '/materials/new',
    name: 'material-new',
    component: () => import('./pages/MaterialFormPage.vue'),
    meta: { permission: 'inventory:write', title: 'inventory.materials.newTitle' },
  },
  {
    path: '/materials/:id',
    name: 'material',
    component: () => import('./pages/MaterialPage.vue'),
    meta: { permission: 'inventory:read', title: 'inventory.materials.detailTitle' },
  },
  // A page and not a dialog: anything that creates a record gets one (§ house
  // rule, see purchasing). Eleven fields scrolling inside a modal is a form
  // fighting its frame.
  {
    path: '/materials/:id/edit',
    name: 'material-edit',
    component: () => import('./pages/MaterialFormPage.vue'),
    meta: { permission: 'inventory:write', title: 'inventory.materials.editTitle' },
  },

  // ── The floor ──────────────────────────────────────────────────────────────
  // Behind `inventory:move`, not `inventory:read`: this is the screen everybody
  // in the warehouse uses, and it shows no prices (backend/domain/Material.js).
  {
    path: '/scan',
    name: 'inventory-scan',
    component: () => import('./pages/ScanPage.vue'),
    meta: { permission: 'inventory:move', title: 'inventory.scan.title' },
  },
  {
    path: '/warehouse',
    name: 'warehouse',
    component: () => import('./pages/WarehousePage.vue'),
    meta: { permission: 'inventory:move', title: 'inventory.locations.title' },
  },

  // ── Goods in ───────────────────────────────────────────────────────────────
  {
    path: '/deliveries',
    name: 'deliveries',
    component: () => import('./pages/DeliveriesPage.vue'),
    meta: { permission: 'inventory:deliveries', title: 'inventory.deliveries.title' },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but new sits first anyway, for the reader.
  {
    path: '/deliveries/new',
    name: 'delivery-new',
    component: () => import('./pages/DeliveryPage.vue'),
    meta: { permission: 'inventory:deliveries', title: 'inventory.deliveries.new' },
  },
  {
    path: '/deliveries/:id',
    name: 'delivery',
    component: () => import('./pages/DeliveryPage.vue'),
    meta: { permission: 'inventory:deliveries', title: 'inventory.deliveries.detailTitle' },
  },
]

/**
 * One row that opens, and not four.
 *
 * The work of this module is spread across three kinds of person, but they are
 * one subject in the sidebar. Giving each a top-level entry would spend four of
 * the few slots the next module installed has the same claim to. The children
 * are ordinary entries and the shell filters each by permission before drawing
 * it — so a fitter with only `inventory:move` gets the map and the scanner, and
 * never learns the catalogue screen exists.
 *
 * The parent points at the MAP and is guarded by `inventory:move`, which needs
 * saying because it looks like the odd choice. The shell draws a parent row as
 * a link to its own `to` and keeps that row visible to anybody who may open one
 * of its children (see app/navigationRules.js). Pointing it at the catalogue
 * would therefore give the warehouse floor a section header that 403s when
 * clicked. The map is the screen both kinds of person can open, so it is the
 * one the section is named after — and `inventory:move` is granted to manager
 * as well as employee for exactly this reason (module.yaml).
 */
export const navigation = [
  {
    to: '/warehouse',
    labelKey: 'inventory.title',
    permission: 'inventory:move',
    icon: 'layers',
    group: 'business',
    children: [
      { to: '/materials', labelKey: 'inventory.materials.title', permission: 'inventory:read' },
      { to: '/scan', labelKey: 'inventory.scan.title', permission: 'inventory:move' },
      { to: '/deliveries', labelKey: 'inventory.deliveries.title', permission: 'inventory:deliveries' },
    ],
  },
]

// Strings this module contributes, merged per locale. Shell keys win on a
// clash, so a module cannot rewrite platform copy.
//
// All four languages, and that is not politeness: a module whose translations
// do not travel with it cannot be sold to the third client. That includes what
// this module is called on the PLATFORM's own screens — Roles, Settings, Audit
// and the assistant panel — because a module that makes somebody add keys to
// the shell is not extractable.
const baseMessages = {
  es: {
    inventory: {
      assistant: {
        move: {
          summary: {
            out: 'Sacar {quantity} {unit} de {material} de {from}: {fromBefore} → {fromAfter} allí',
            in: 'Meter {quantity} {unit} de {material} en {to}: {toBefore} → {toAfter} allí',
            between: 'Llevar {quantity} {unit} de {material} de {from} a {to}',
            fallback: 'Registrar un movimiento de {quantity}',
          },
          action: {
            out: 'Registrar consumo de material',
            in: 'Registrar entrada de material',
            between: 'Mover material entre ubicaciones',
          },
          field: {
            material: 'Material',
            quantity: 'Cantidad',
            from: 'Origen',
            to: 'Destino',
            reason: 'Motivo',
          },
          value: {
            quantity: '{quantity} {unit}',
          },
          where: 'Ubicación',
          after: {
            out: 'Quedarán {fromAfter} {unit} en {from}',
            in: 'Habrá {toAfter} {unit} en {to}',
            between: 'Quedarán {fromAfter} {unit} en {from} y {toAfter} en {to}',
          },
          outcome: {
            done: 'Movimiento registrado · {stock} en total',
            below: 'Movimiento registrado · {stock} en total, por debajo del mínimo',
          },
        },
      },
      title: 'Almacén',
      dashboard: {
        belowMinimum: 'Bajo mínimos',
        belowMinimumHelp: 'Cuántas referencias tienen menos existencias que su mínimo configurado.',
        outOfStock: 'Sin existencias',
        outOfStockHelp: 'Cuántas referencias están a cero o en negativo, tengan mínimo o no.',
        value: 'Valor del almacén',
        valueHelp: 'Lo que vale lo que hay en estantería, a precio de coste, en euros.',
        materials: 'Referencias',
        materialsHelp: 'Cuántos materiales hay en el catálogo, sin contar los archivados.',
        toBuy: 'Para reponer',
        toBuyHelp: 'Los materiales por debajo de su mínimo, con lo que queda de cada uno. Estira la tarjeta y caben más.',
      },
      saved: 'Guardado',
      materials: {
        title: 'Materiales',
        subtitle: 'Qué tenéis, cuánto queda y dónde está',
        detailTitle: 'Material',
        new: 'Nuevo material',
        newTitle: 'Nuevo material',
        editTitle: 'Editar material',
        formSubtitle: 'Qué es, qué cuesta y de quién viene',
        whatItIs: 'Qué es',
        whatItCosts: 'Qué cuesta',
        whereItComesFrom: 'De dónde viene',
        label: 'Etiqueta',
        printLabels: 'Imprimir {count} etiquetas',
        empty: {
          none: 'Todavía no hay materiales',
          noneHint: 'Da de alta el primero, o sube un albarán y créalos desde ahí.',
          filtered: 'Sin resultados',
          filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
        },
      },
      material: {
        whereIsIt: '¿Dónde está?',
        nowhere: 'No hay nada en ninguna ubicación',
        nowhereHint: 'Registra una entrada para que empiece a haber stock.',
        details: 'Ficha',
        history: 'Movimientos',
        noHistory: 'Todavía no se ha movido nada',
      },
      locations: {
        title: 'Ubicaciones',
        subtitle: 'El mapa del almacén: naves, pasillos, estanterías, furgonetas y obras',
        map: 'Mapa',
        new: 'Nueva ubicación',
        edit: 'Editar ubicación',
        empty: 'El mapa está vacío',
        emptyHint: 'Empieza por la nave, y dentro los pasillos y las estanterías.',
        pickOne: 'Elige una ubicación',
        pickOneHint: 'Selecciónala en el mapa para ver qué hay dentro.',
        nothingHere: 'Aquí no hay nada',
        includeChildren: 'Incluir lo que hay debajo',
        makeDefault: 'Hacer que sea la de por defecto',
        default: 'Por defecto',
        defaultSet: 'Ya es la ubicación por defecto',
        archive: 'Archivar',
        archived: 'Ubicación archivada',
        archiveConfirm: '¿Archivar esta ubicación?',
        archiveConfirmHint: 'Las etiquetas que ya estén pegadas en ella dejarán de encontrar nada.',
        printLabels: 'Imprimir {count} etiquetas',
      },
      locationKinds: {
        warehouse: 'Nave', zone: 'Pasillo', shelf: 'Estantería',
        bin: 'Balda', vehicle: 'Furgoneta', site: 'Obra',
      },
      units: {
        unit: 'ud.', box: 'caja', pallet: 'palet', set: 'juego', roll: 'rollo',
        kg: 'kg', g: 'g', l: 'l', m: 'm', m2: 'm²', m3: 'm³',
      },
      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Nombre, código, referencia…',
        category: 'Familia',
        allCategories: 'Todas',
        location: 'Ubicación',
        anywhere: 'En cualquier sitio',
        state: 'Estado',
        allStates: 'Todos',
      },
      state: { ok: 'Correcto', belowMinimum: 'Bajo mínimo', outOfStock: 'Sin stock' },
      table: {
        material: 'Material', category: 'Familia', stock: 'Stock', minimum: 'Mínimo',
        cost: 'Coste', value: 'Valor', state: 'Estado', location: 'Ubicación',
        quantity: 'Cantidad', movement: 'Movimiento', route: 'Recorrido', after: 'Queda',
        reason: 'Motivo', who: 'Quién', when: 'Cuándo', here: 'Aquí', everywhere: 'En total',
      },
      kpi: {
        materials: 'Materiales', value: 'Valor del stock',
        belowMinimum: 'Bajo mínimo', outOfStock: 'Sin stock', inStock: 'Hay',
      },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },
      fields: {
        name: 'Nombre', description: 'Descripción', category: 'Familia', noCategory: 'Sin familia',
        unit: 'Unidad', costPrice: 'Precio de coste', salePrice: 'Precio de venta',
        minStock: 'Mínimo', minStockHelp: 'Por debajo de esto avisa a quien compra. 0 = no avisar.',
        leadTimeDays: 'Plazo (días)',
        supplierRef: 'Referencia del proveedor',
        supplierRefHelp: 'Su código. Es lo primero que se busca al leer un albarán suyo.',
        barcode: 'Código de barras', barcodeHelp: 'El EAN de la caja, si lo trae impreso.',
        supplier: 'Proveedor',
        supplierHelp: 'Si está el módulo de compras, se elige de su ficha; si no, se escribe.',
        supplierPlaceholder: 'Empieza a escribir el nombre…',
        supplierLinked: 'Enlazado a su ficha de proveedor',
        supplierUnlink: 'Desenlazar de la ficha',
        code: 'Código', codeHelp: 'Lo que va a decir el QR. Usa el que ya esté pintado en el pasillo.',
        codePlaceholder: 'A-03-2',
        kind: 'Tipo', parent: 'Dentro de', parentHelp: 'La estantería va dentro del pasillo, y el pasillo dentro de la nave.',
        noParent: 'En la raíz',
      },
      move: {
        open: 'Mover stock',
        from: 'Desde', to: 'Hasta', pickLocation: 'Elige una ubicación',
        quantity: 'Cantidad ({unit})',
        availableHere: 'Ahí hay {quantity}.',
        reason: 'Motivo', reasonRequired: 'Motivo (obligatorio)',
        reasonPlaceholder: 'Para la obra de Puigcerdà, recuento anual…',
        adjustmentNote: 'Un ajuste es corregir el sistema con lo que has contado. Queda con tu nombre.',
        record: 'Registrar', done: 'Movimiento registrado',
        kind: { in: 'Entrada', out: 'Salida', transfer: 'Traspaso', adjustment: 'Ajuste' },
        title: {
          in: 'Registrar una entrada', out: 'Registrar una salida',
          transfer: 'Traspasar entre ubicaciones', adjustment: 'Corregir el recuento',
        },
      },
      scan: {
        title: 'Escanear',
        subtitle: 'Apunta a una estantería o a un material',
        aim: 'Encuadra el código',
        typeLabel: 'O teclea el código',
        typePlaceholder: 'A-03-2, MAT-0184…',
        look: 'Buscar',
        noReader: 'Este navegador no sabe leer códigos. Teclea el código o usa un lector de mano.',
        unknown: 'Nadie conoce el código {code}',
        unknownHint: 'No es ninguna ubicación ni ningún material de este almacén.',
        inTotal: 'En total hay',
        openCard: 'Abrir la ficha',
        recent: 'Últimos escaneos',
      },
      labels: {
        title: 'Imprimir etiquetas',
        size: 'Tamaño de etiqueta', width: 'Ancho (mm)', height: 'Alto (mm)',
        padding: 'Margen interior (mm)', copies: 'Copias de cada una',
        custom: 'Personalizado…',
        tally: '{items} elementos · {labels} etiquetas · {width} × {height} mm',
        many: 'Vas a imprimir {count} etiquetas. Generarlas tarda unos segundos; no cierres la ventana.',
        empty: 'No has seleccionado nada',
        andMore: 'y {count} más',
        print: 'Imprimir {count}', printTest: 'Imprimir prueba',
        help: {
          summary: '¿No sale bien en la impresora térmica?',
          printer: 'En el diálogo de impresión, elige la impresora térmica, no la normal.',
          paper: 'Papel: el de la etiqueta cargada, o «{width} × {height} mm».',
          margins: 'Márgenes: ninguno. Escala: 100 %, no «Ajustar».',
          headers: 'Desactiva encabezados y pies de página.',
          test: 'Imprime una prueba antes de la tirada entera.',
        },
      },
      deliveries: {
        title: 'Albaranes',
        subtitle: 'Lo que ha entrado por la puerta',
        detailTitle: 'Albarán',
        new: 'Nuevo albarán',
        wizardSubtitle: 'Hazle una foto y repásalo',
        searchPlaceholder: 'Número, proveedor…',
        empty: 'Todavía no hay albaranes',
        emptyHint: 'Fotografía el primero y el sistema lo lee.',
        noNumber: 'Sin número',
        unmatched: '{count} sin material',
        status: { draft: 'Por repasar', confirmed: 'Confirmado', cancelled: 'Descartado' },
        table: { number: 'Albarán', supplier: 'Proveedor', date: 'Fecha', lines: 'Líneas', total: 'Total' },
        intakeLead: 'Hazle una foto al albarán y lo leemos por ti.',
        takePhoto: 'Hacer foto', takePhotoHint: 'Cámara trasera',
        uploadFile: 'Subir imagen', uploadFileHint: 'JPG, PNG o WEBP',
        pdfNote: 'De momento solo se leen fotos. Si te lo han mandado en PDF, ábrelo y hazle una foto a la pantalla.',
        reading: 'Leyendo el albarán…',
        typeInstead: 'Prefiero teclearlo',
        header: 'Cabecera',
        receivedBy: 'Recibido por',
        lines: 'Líneas',
        unmatchedBanner: 'Quedan {count} líneas sin material. Esas no entrarán en stock.',
        allMatched: 'Todas las líneas tienen material. Se puede confirmar.',
        material: 'Material', pickMaterial: 'Elige el material',
        unitPrice: 'Precio unidad',
        destination: 'Dónde va', destinationHelp: 'Si lo dejas vacío, va a la ubicación por defecto.',
        defaultLocation: 'Ubicación por defecto',
        addLine: 'Añadir línea',
        rematch: 'Volver a buscar material',
        confirm: 'Confirmar {count} líneas',
        confirmTitle: '¿Confirmar el albarán?',
        confirmMessage: 'Van a entrar {count} líneas en stock. Después ya no se puede editar.',
        confirmWithUnmatched: 'Entrarán {stocked} líneas. Las {skipped} que no tienen material se quedan fuera.',
        confirmed: 'Albarán confirmado: {count} movimientos',
        alreadyDone: 'Ese albarán ya estaba confirmado',
        confirmedOn: 'Confirmado el {at}. El stock ya se movió.',
        cancelledBecause: 'Descartado. Motivo: {reason}',
        discard: 'Descartar', discardTitle: '¿Descartar este albarán?',
        discardMessage: 'No entrará nada en stock. Se queda guardado por si hay que consultarlo.',
        truncated: 'El albarán era largo y puede que falten líneas al final. Repásalas.',
        confidence: { high: 'Se lee bien', medium: 'Dudoso', low: 'Casi ilegible' },
        matchLayer: {
          reference: 'Por referencia', alias: 'Aprendido', similarity: 'Por parecido',
          ai: 'Propuesto por IA', manual: 'A mano', created: 'Creado',
        },
      },
      notifications: {
        belowMinimum: 'Material por debajo del mínimo',
        lowStock: {
          title: 'Material por debajo del mínimo',
          body: '{material}: quedan {stock} y el mínimo son {minimum}.',
        },
      },
    },

    // ── Lo que este módulo se llama en las pantallas de la plataforma ─────────
    // Roles, ajustes y auditoría son de la plataforma, pero los textos que
    // hablan del almacén son de este módulo. Van aquí para que instalarlo deje
    // esas pantallas completas sin tocar la plataforma.
    // Los códigos de error de este módulo, en la rama donde la plataforma los
    // busca: errorMessage() traduce por `errors.<CODE>` y fieldErrors() por
    // `errors.fields.<CODE>`. Se mezclan rama a rama con los del shell, que
    // gana en caso de choque — así un módulo añade códigos sin poder reescribir
    // los de la plataforma.
    errors: {
      DUPLICATE_NAME: 'Ya hay una categoría con ese nombre.',
      PARENT_ARCHIVED: 'El sitio del que cuelga este está archivado. Recupera ese primero.',
      INSUFFICIENT_STOCK: 'No hay tanto en esa ubicación',
      DUPLICATE_CODE: 'Ese código ya está usado',
      DUPLICATE_SUPPLIER_REF: 'Otro material ya usa esa referencia de proveedor',
      HAS_STOCK: 'Todavía tiene stock. Sácalo antes de archivarlo.',
      HAS_CHILDREN: 'Todavía tiene ubicaciones dentro',
      HAS_MATERIALS: 'Todavía tiene materiales dentro',
      IS_DEFAULT: 'Es la ubicación por defecto. Nombra otra primero.',
      ALREADY_CONFIRMED: 'Ese albarán ya está confirmado',
      NOTHING_TO_STOCK: 'Ninguna línea tiene material y cantidad',
      CONFIRMING: 'Ese albarán se está confirmando ahora mismo',
      PARTIALLY_STOCKED: 'Parte de este albarán ya está en stock. Vuelve a confirmarlo para terminarlo.',
      NO_DEFAULT_LOCATION: 'No hay ninguna ubicación por defecto',
      UNSUPPORTED_DOCUMENT: 'Solo se pueden leer fotos. Hazle una foto al albarán.',
      UNREADABLE_DOCUMENT: 'No se ha podido leer. Prueba con otra foto.',
      fields: {
        SAME_LOCATION: 'El origen y el destino son el mismo sitio',
        INSUFFICIENT_STOCK: 'Solo hay {available}',
        ARCHIVED: 'Está archivado',
        NOT_FOUND: 'No existe',
        UNSUPPORTED_TYPE: 'Ese tipo de archivo no se puede leer',
      },
    },
    modules: { inventory: 'Almacén' },
    assistant: {
      openers: {
        inventory: {
          'below-minimum': '¿Qué material está por debajo del mínimo?',
          'stock-value': '¿Cuánto vale el stock que tenemos?',
          'pending-deliveries': '¿Qué albaranes quedan por revisar?',
        },
      },
    },
    permissions: {
      inventory: {
        read: 'Ver el catálogo con sus precios',
        write: 'Dar de alta y editar materiales',
        move: 'Escanear y mover stock',
        deliveries: 'Subir y confirmar albaranes',
        locations: 'Mantener el mapa del almacén',
      },
    },
    settings: {
      ns: {
        inventory: {
          title: 'Almacén',
          subtitle: 'Cómo funciona este almacén en concreto',
          allowNegativeStock: {
            label: 'Dejar que una ubicación baje de cero',
            help: 'Desactivado, una salida mayor de lo que hay se rechaza. Actívalo si el albarán llega días después que el material: si no, el montador no puede apuntar lo que se ha llevado.',
          },
          labelSize: {
            label: 'Tamaño de etiqueta por defecto (mm)',
            help: 'El rollo que tiene comprado este almacén. Quien imprima puede cambiarlo para una tirada.',
          },
        },
      },
    },
    audit: {
      actions: {
        inventory: {
          material_created: 'Material dado de alta',
          material_updated: 'Material modificado',
          material_archived: 'Material archivado',
          material_restored: 'Material devuelto al catálogo',
          category_created: 'Familia creada',
          category_updated: 'Familia modificada',
          category_archived: 'Familia archivada',
          category_restored: 'Familia devuelta al catálogo',
          location_created: 'Ubicación creada',
          location_updated: 'Ubicación modificada',
          location_archived: 'Ubicación archivada',
          location_restored: 'Ubicación devuelta al almacén',
          location_default_set: 'Ubicación por defecto cambiada',
          stock_in: 'Entrada de material',
          stock_out: 'Salida de material',
          stock_transfer: 'Traspaso entre ubicaciones',
          stock_adjustment: 'Ajuste de recuento',
          delivery_created: 'Albarán abierto',
          delivery_updated: 'Albarán corregido',
          delivery_confirmed: 'Albarán confirmado en stock',
          delivery_cancelled: 'Albarán descartado',
        },
      },
    },
  },

  ca: {
    inventory: {
      assistant: {
        move: {
          summary: {
            out: 'Treure {quantity} {unit} de {material} de {from}: {fromBefore} → {fromAfter} allà',
            in: 'Posar {quantity} {unit} de {material} a {to}: {toBefore} → {toAfter} allà',
            between: 'Portar {quantity} {unit} de {material} de {from} a {to}',
            fallback: 'Registrar un moviment de {quantity}',
          },
          action: {
            out: 'Registrar consum de material',
            in: 'Registrar entrada de material',
            between: 'Moure material entre ubicacions',
          },
          field: {
            material: 'Material',
            quantity: 'Quantitat',
            from: 'Origen',
            to: 'Destinació',
            reason: 'Motiu',
          },
          value: {
            quantity: '{quantity} {unit}',
          },
          where: 'Ubicació',
          after: {
            out: 'En quedaran {fromAfter} {unit} a {from}',
            in: 'N’hi haurà {toAfter} {unit} a {to}',
            between: 'En quedaran {fromAfter} {unit} a {from} i {toAfter} a {to}',
          },
          outcome: {
            done: 'Moviment registrat · {stock} en total',
            below: 'Moviment registrat · {stock} en total, per sota del mínim',
          },
        },
      },
      title: 'Magatzem',
      dashboard: {
        belowMinimum: 'Sota mínims',
        belowMinimumHelp: 'Quantes referències tenen menys existències que el seu mínim configurat.',
        outOfStock: 'Sense existències',
        outOfStockHelp: 'Quantes referències estan a zero o en negatiu, tinguin mínim o no.',
        value: 'Valor del magatzem',
        valueHelp: 'El que val el que hi ha a la prestatgeria, a preu de cost, en euros.',
        materials: 'Referències',
        materialsHelp: 'Quants materials hi ha al catàleg, sense comptar els arxivats.',
        toBuy: 'Per reposar',
        toBuyHelp: 'Els materials per sota del seu mínim, amb el que queda de cadascun. Estira la targeta i n\'hi caben més.',
      },
      saved: 'Desat',
      materials: {
        title: 'Materials',
        subtitle: 'Què teniu, quant en queda i on és',
        detailTitle: 'Material',
        new: 'Nou material',
        newTitle: 'Nou material',
        editTitle: 'Edita el material',
        formSubtitle: 'Què és, què costa i de qui ve',
        whatItIs: 'Què és',
        whatItCosts: 'Què costa',
        whereItComesFrom: 'D’on ve',
        label: 'Etiqueta',
        printLabels: 'Imprimeix {count} etiquetes',
        empty: {
          none: 'Encara no hi ha materials',
          noneHint: "Dona d'alta el primer, o puja un albarà i crea'ls des d'allà.",
          filtered: 'Sense resultats',
          filteredHint: 'Prova de treure algun filtre o de buscar una altra cosa.',
        },
      },
      material: {
        whereIsIt: 'On és?',
        nowhere: 'No hi ha res a cap ubicació',
        nowhereHint: 'Registra una entrada perquè comenci a haver-hi estoc.',
        details: 'Fitxa',
        history: 'Moviments',
        noHistory: "Encara no s'ha mogut res",
      },
      locations: {
        title: 'Ubicacions',
        subtitle: 'El mapa del magatzem: naus, passadissos, prestatgeries, furgonetes i obres',
        map: 'Mapa',
        new: 'Nova ubicació',
        edit: 'Edita la ubicació',
        empty: 'El mapa és buit',
        emptyHint: 'Comença per la nau, i a dins els passadissos i les prestatgeries.',
        pickOne: 'Tria una ubicació',
        pickOneHint: 'Selecciona-la al mapa per veure què hi ha a dins.',
        nothingHere: 'Aquí no hi ha res',
        includeChildren: 'Inclou el que hi ha a sota',
        makeDefault: 'Fes que sigui la per defecte',
        default: 'Per defecte',
        defaultSet: 'Ja és la ubicació per defecte',
        archive: 'Arxiva',
        archived: 'Ubicació arxivada',
        archiveConfirm: 'Vols arxivar aquesta ubicació?',
        archiveConfirmHint: 'Les etiquetes que ja hi siguin enganxades deixaran de trobar res.',
        printLabels: 'Imprimeix {count} etiquetes',
      },
      locationKinds: {
        warehouse: 'Nau', zone: 'Passadís', shelf: 'Prestatgeria',
        bin: 'Lleixa', vehicle: 'Furgoneta', site: 'Obra',
      },
      units: {
        unit: 'u.', box: 'caixa', pallet: 'palet', set: 'joc', roll: 'rotlle',
        kg: 'kg', g: 'g', l: 'l', m: 'm', m2: 'm²', m3: 'm³',
      },
      filters: {
        search: 'Cerca',
        searchPlaceholder: 'Nom, codi, referència…',
        category: 'Família',
        allCategories: 'Totes',
        location: 'Ubicació',
        anywhere: 'A qualsevol lloc',
        state: 'Estat',
        allStates: 'Tots',
      },
      state: { ok: 'Correcte', belowMinimum: 'Sota mínim', outOfStock: 'Sense estoc' },
      table: {
        material: 'Material', category: 'Família', stock: 'Estoc', minimum: 'Mínim',
        cost: 'Cost', value: 'Valor', state: 'Estat', location: 'Ubicació',
        quantity: 'Quantitat', movement: 'Moviment', route: 'Recorregut', after: 'En queda',
        reason: 'Motiu', who: 'Qui', when: 'Quan', here: 'Aquí', everywhere: 'En total',
      },
      kpi: {
        materials: 'Materials', value: "Valor de l'estoc",
        belowMinimum: 'Sota mínim', outOfStock: 'Sense estoc', inStock: 'Hi ha',
      },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      fields: {
        name: 'Nom', description: 'Descripció', category: 'Família', noCategory: 'Sense família',
        unit: 'Unitat', costPrice: 'Preu de cost', salePrice: 'Preu de venda',
        minStock: 'Mínim', minStockHelp: 'Per sota d\'això avisa qui compra. 0 = no avisar.',
        leadTimeDays: 'Termini (dies)',
        supplierRef: 'Referència del proveïdor',
        supplierRefHelp: 'El seu codi. És el primer que es busca en llegir un albarà seu.',
        barcode: 'Codi de barres', barcodeHelp: "L'EAN de la caixa, si el porta imprès.",
        supplier: 'Proveïdor',
        supplierHelp: 'Si hi ha el mòdul de compres, es tria de la seva fitxa; si no, es tecleja.',
        supplierPlaceholder: 'Comença a escriure el nom…',
        supplierLinked: 'Enllaçat a la seva fitxa de proveïdor',
        supplierUnlink: 'Desenllaça de la fitxa',
        code: 'Codi', codeHelp: 'El que dirà el QR. Fes servir el que ja hi ha pintat al passadís.',
        codePlaceholder: 'A-03-2',
        kind: 'Tipus', parent: 'Dins de', parentHelp: 'La prestatgeria va dins del passadís, i el passadís dins de la nau.',
        noParent: "A l'arrel",
      },
      move: {
        open: 'Mou estoc',
        from: 'Des de', to: 'Fins a', pickLocation: 'Tria una ubicació',
        quantity: 'Quantitat ({unit})',
        availableHere: "Allà n'hi ha {quantity}.",
        reason: 'Motiu', reasonRequired: 'Motiu (obligatori)',
        reasonPlaceholder: "Per l'obra de Puigcerdà, recompte anual…",
        adjustmentNote: 'Un ajust és corregir el sistema amb el que has comptat. Queda amb el teu nom.',
        record: 'Registra', done: 'Moviment registrat',
        kind: { in: 'Entrada', out: 'Sortida', transfer: 'Traspàs', adjustment: 'Ajust' },
        title: {
          in: 'Registra una entrada', out: 'Registra una sortida',
          transfer: 'Traspassa entre ubicacions', adjustment: 'Corregeix el recompte',
        },
      },
      scan: {
        title: 'Escaneja',
        subtitle: 'Apunta a una prestatgeria o a un material',
        aim: 'Enquadra el codi',
        typeLabel: 'O escriu el codi',
        typePlaceholder: 'A-03-2, MAT-0184…',
        look: 'Cerca',
        noReader: 'Aquest navegador no sap llegir codis. Escriu el codi o fes servir un lector de mà.',
        unknown: 'Ningú coneix el codi {code}',
        unknownHint: "No és cap ubicació ni cap material d'aquest magatzem.",
        inTotal: 'En total hi ha',
        openCard: 'Obre la fitxa',
        recent: 'Últims escanejos',
      },
      labels: {
        title: 'Imprimeix etiquetes',
        size: "Mida d'etiqueta", width: 'Amplada (mm)', height: 'Alçada (mm)',
        padding: 'Marge interior (mm)', copies: 'Còpies de cadascuna',
        custom: 'Personalitzat…',
        tally: '{items} elements · {labels} etiquetes · {width} × {height} mm',
        many: 'Imprimiràs {count} etiquetes. Generar-les triga uns segons; no tanquis la finestra.',
        empty: 'No has seleccionat res',
        andMore: 'i {count} més',
        print: 'Imprimeix {count}', printTest: 'Imprimeix una prova',
        help: {
          summary: 'No surt bé a la impressora tèrmica?',
          printer: "Al diàleg d'impressió, tria la impressora tèrmica, no la normal.",
          paper: "Paper: el de l'etiqueta carregada, o «{width} × {height} mm».",
          margins: 'Marges: cap. Escala: 100 %, no «Ajusta».',
          headers: 'Desactiva capçaleres i peus de pàgina.',
          test: 'Imprimeix una prova abans de la tirada sencera.',
        },
      },
      deliveries: {
        title: 'Albarans',
        subtitle: 'El que ha entrat per la porta',
        detailTitle: 'Albarà',
        new: 'Nou albarà',
        wizardSubtitle: 'Fes-li una foto i repassa-ho',
        searchPlaceholder: 'Número, proveïdor…',
        empty: 'Encara no hi ha albarans',
        emptyHint: 'Fotografia el primer i el sistema el llegeix.',
        noNumber: 'Sense número',
        unmatched: '{count} sense material',
        status: { draft: 'Per repassar', confirmed: 'Confirmat', cancelled: 'Descartat' },
        table: { number: 'Albarà', supplier: 'Proveïdor', date: 'Data', lines: 'Línies', total: 'Total' },
        intakeLead: "Fes una foto de l'albarà i el llegim per tu.",
        takePhoto: 'Fes una foto', takePhotoHint: 'Càmera del darrere',
        uploadFile: 'Puja una imatge', uploadFileHint: 'JPG, PNG o WEBP',
        pdfNote: "De moment només es llegeixen fotos. Si te l'han enviat en PDF, obre'l i fes una foto de la pantalla.",
        reading: "Llegint l'albarà…",
        typeInstead: 'Prefereixo escriure-ho',
        header: 'Capçalera',
        receivedBy: 'Rebut per',
        lines: 'Línies',
        unmatchedBanner: 'Queden {count} línies sense material. Aquestes no entraran a estoc.',
        allMatched: 'Totes les línies tenen material. Es pot confirmar.',
        material: 'Material', pickMaterial: 'Tria el material',
        unitPrice: 'Preu unitat',
        destination: 'On va', destinationHelp: 'Si ho deixes buit, va a la ubicació per defecte.',
        defaultLocation: 'Ubicació per defecte',
        addLine: 'Afegeix una línia',
        rematch: 'Torna a buscar material',
        confirm: 'Confirma {count} línies',
        confirmTitle: "Vols confirmar l'albarà?",
        confirmMessage: 'Entraran {count} línies a estoc. Després ja no es pot editar.',
        confirmWithUnmatched: 'Entraran {stocked} línies. Les {skipped} que no tenen material es queden fora.',
        confirmed: 'Albarà confirmat: {count} moviments',
        alreadyDone: 'Aquest albarà ja estava confirmat',
        confirmedOn: "Confirmat el {at}. L'estoc ja s'ha mogut.",
        cancelledBecause: 'Descartat. Motiu: {reason}',
        discard: 'Descarta', discardTitle: 'Vols descartar aquest albarà?',
        discardMessage: 'No entrarà res a estoc. Es desa per si cal consultar-lo.',
        truncated: 'Era llarg i pot ser que hi faltin línies al final. Repassa-les.',
        confidence: { high: 'Es llegeix bé', medium: 'Dubtós', low: 'Gairebé il·legible' },
        matchLayer: {
          reference: 'Per referència', alias: 'Après', similarity: 'Per semblança',
          ai: 'Proposat per IA', manual: 'A mà', created: 'Creat',
        },
      },
      notifications: {
        belowMinimum: 'Material per sota del mínim',
        lowStock: {
          title: 'Material per sota del mínim',
          body: '{material}: en queden {stock} i el mínim és {minimum}.',
        },
      },
    },

    errors: {
      DUPLICATE_NAME: 'Ja hi ha una categoria amb aquest nom.',
      PARENT_ARCHIVED: 'El lloc del qual penja aquest està arxivat. Recupera aquell primer.',
      INSUFFICIENT_STOCK: "No n'hi ha tant en aquesta ubicació",
      DUPLICATE_CODE: 'Aquest codi ja està fet servir',
      DUPLICATE_SUPPLIER_REF: 'Un altre material ja fa servir aquesta referència',
      HAS_STOCK: "Encara té estoc. Treu-lo abans d'arxivar-lo.",
      HAS_CHILDREN: 'Encara té ubicacions a dins',
      HAS_MATERIALS: 'Encara té materials a dins',
      IS_DEFAULT: "És la ubicació per defecte. Nomena'n una altra primer.",
      ALREADY_CONFIRMED: 'Aquest albarà ja està confirmat',
      NOTHING_TO_STOCK: 'Cap línia té material i quantitat',
      CONFIRMING: "Aquest albarà s'està confirmant ara mateix",
      PARTIALLY_STOCKED: "Part d'aquest albarà ja és a l'estoc. Torna'l a confirmar per acabar-lo.",
      NO_DEFAULT_LOCATION: 'No hi ha cap ubicació per defecte',
      UNSUPPORTED_DOCUMENT: "Només es poden llegir fotos. Fes una foto de l'albarà.",
      UNREADABLE_DOCUMENT: "No s'ha pogut llegir. Prova amb una altra foto.",
      fields: {
        SAME_LOCATION: "L'origen i el destí són el mateix lloc",
        INSUFFICIENT_STOCK: "Només n'hi ha {available}",
        ARCHIVED: 'Està arxivat',
        NOT_FOUND: 'No existeix',
        UNSUPPORTED_TYPE: 'Aquest tipus de fitxer no es pot llegir',
      },
    },
    modules: { inventory: 'Magatzem' },
    assistant: {
      openers: {
        inventory: {
          'below-minimum': 'Quin material està per sota del mínim?',
          'stock-value': "Quant val l'estoc que tenim?",
          'pending-deliveries': 'Quins albarans queden per revisar?',
        },
      },
    },
    permissions: {
      inventory: {
        read: 'Veure el catàleg amb els preus',
        write: "Donar d'alta i editar materials",
        move: 'Escanejar i moure estoc',
        deliveries: 'Pujar i confirmar albarans',
        locations: 'Mantenir el mapa del magatzem',
      },
    },
    settings: {
      ns: {
        inventory: {
          title: 'Magatzem',
          subtitle: 'Com funciona aquest magatzem en concret',
          allowNegativeStock: {
            label: 'Deixar que una ubicació baixi de zero',
            help: "Desactivat, una sortida més gran del que hi ha es rebutja. Activa'l si l'albarà arriba dies després que el material: si no, el muntador no pot apuntar el que s'ha endut.",
          },
          labelSize: {
            label: "Mida d'etiqueta per defecte (mm)",
            help: 'El rotlle que té comprat aquest magatzem. Qui imprimeixi pot canviar-lo per a una tirada.',
          },
        },
      },
    },
    audit: {
      actions: {
        inventory: {
          material_created: "Material donat d'alta",
          material_updated: 'Material modificat',
          material_archived: 'Material arxivat',
          material_restored: 'Material retornat al catàleg',
          category_created: 'Família creada',
          category_updated: 'Família modificada',
          category_archived: 'Família arxivada',
          category_restored: 'Família retornada al catàleg',
          location_created: 'Ubicació creada',
          location_updated: 'Ubicació modificada',
          location_archived: 'Ubicació arxivada',
          location_restored: 'Ubicació retornada al magatzem',
          location_default_set: 'Ubicació per defecte canviada',
          stock_in: 'Entrada de material',
          stock_out: 'Sortida de material',
          stock_transfer: 'Traspàs entre ubicacions',
          stock_adjustment: 'Ajust de recompte',
          delivery_created: 'Albarà obert',
          delivery_updated: 'Albarà corregit',
          delivery_confirmed: 'Albarà confirmat a estoc',
          delivery_cancelled: 'Albarà descartat',
        },
      },
    },
  },

  en: {
    inventory: {
      assistant: {
        move: {
          summary: {
            out: 'Take {quantity} {unit} of {material} out of {from}: {fromBefore} → {fromAfter} left there',
            in: 'Put {quantity} {unit} of {material} into {to}: {toBefore} → {toAfter} there',
            between: 'Move {quantity} {unit} of {material} from {from} to {to}',
            fallback: 'Record a stock movement of {quantity}',
          },
          action: {
            out: 'Record material used',
            in: 'Record material received',
            between: 'Move material between locations',
          },
          field: {
            material: 'Material',
            quantity: 'Quantity',
            from: 'From',
            to: 'To',
            reason: 'Reason',
          },
          value: {
            quantity: '{quantity} {unit}',
          },
          where: 'Location',
          after: {
            out: '{fromAfter} {unit} left in {from}',
            in: '{toAfter} {unit} in {to}',
            between: '{fromAfter} {unit} left in {from}, {toAfter} in {to}',
          },
          outcome: {
            done: 'Movement recorded · {stock} in total',
            below: 'Movement recorded · {stock} in total, below the minimum',
          },
        },
      },
      title: 'Warehouse',
      dashboard: {
        belowMinimum: 'Below minimum',
        belowMinimumHelp: 'How many references hold less stock than the minimum set for them.',
        outOfStock: 'Out of stock',
        outOfStockHelp: 'How many references are at zero or below, whether or not they have a minimum.',
        value: 'Warehouse value',
        valueHelp: 'What is on the shelf is worth, at cost price, in euros.',
        materials: 'References',
        materialsHelp: 'How many materials are in the catalogue, archived ones excluded.',
        toBuy: 'To restock',
        toBuyHelp: 'The materials below their minimum, with how much is left of each. Stretch the card and more fit.',
      },
      saved: 'Saved',
      materials: {
        title: 'Materials',
        subtitle: 'What you keep, how much is left and where it is',
        detailTitle: 'Material',
        new: 'New material',
        newTitle: 'New material',
        editTitle: 'Edit material',
        formSubtitle: 'What it is, what it costs and who it comes from',
        whatItIs: 'What it is',
        whatItCosts: 'What it costs',
        whereItComesFrom: 'Where it comes from',
        label: 'Label',
        printLabels: 'Print {count} labels',
        empty: {
          none: 'No materials yet',
          noneHint: 'Add the first one, or upload a delivery note and create them from it.',
          filtered: 'Nothing found',
          filteredHint: 'Try removing a filter or searching for something else.',
        },
      },
      material: {
        whereIsIt: 'Where is it?',
        nowhere: 'Nothing in any location',
        nowhereHint: 'Record an entry so there is stock to find.',
        details: 'Details',
        history: 'Movements',
        noHistory: 'Nothing has moved yet',
      },
      locations: {
        title: 'Locations',
        subtitle: 'The warehouse map: buildings, aisles, shelves, vans and sites',
        map: 'Map',
        new: 'New location',
        edit: 'Edit location',
        empty: 'The map is empty',
        emptyHint: 'Start with the building, then the aisles and shelves inside it.',
        pickOne: 'Pick a location',
        pickOneHint: 'Select it on the map to see what is inside.',
        nothingHere: 'Nothing here',
        includeChildren: 'Include everything below',
        makeDefault: 'Make this the default',
        default: 'Default',
        defaultSet: 'This is now the default location',
        archive: 'Archive',
        archived: 'Location archived',
        archiveConfirm: 'Archive this location?',
        archiveConfirmHint: 'Labels already stuck to it will stop finding anything.',
        printLabels: 'Print {count} labels',
      },
      locationKinds: {
        warehouse: 'Building', zone: 'Aisle', shelf: 'Shelf',
        bin: 'Bin', vehicle: 'Van', site: 'Site',
      },
      units: {
        unit: 'ea.', box: 'box', pallet: 'pallet', set: 'set', roll: 'roll',
        kg: 'kg', g: 'g', l: 'l', m: 'm', m2: 'm²', m3: 'm³',
      },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Name, code, reference…',
        category: 'Family',
        allCategories: 'All',
        location: 'Location',
        anywhere: 'Anywhere',
        state: 'State',
        allStates: 'All',
      },
      state: { ok: 'Fine', belowMinimum: 'Below minimum', outOfStock: 'Out of stock' },
      table: {
        material: 'Material', category: 'Family', stock: 'Stock', minimum: 'Minimum',
        cost: 'Cost', value: 'Value', state: 'State', location: 'Location',
        quantity: 'Quantity', movement: 'Movement', route: 'Route', after: 'Left',
        reason: 'Reason', who: 'Who', when: 'When', here: 'Here', everywhere: 'In total',
      },
      kpi: {
        materials: 'Materials', value: 'Stock value',
        belowMinimum: 'Below minimum', outOfStock: 'Out of stock', inStock: 'In stock',
      },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      fields: {
        name: 'Name', description: 'Description', category: 'Family', noCategory: 'No family',
        unit: 'Unit', costPrice: 'Cost price', salePrice: 'Sale price',
        minStock: 'Minimum', minStockHelp: 'Below this, whoever buys is warned. 0 = never warn.',
        leadTimeDays: 'Lead time (days)',
        supplierRef: 'Supplier reference',
        supplierRefHelp: 'Their code. It is the first thing looked for when reading their delivery note.',
        barcode: 'Barcode', barcodeHelp: 'The EAN on the box, when it comes printed.',
        supplier: 'Supplier',
        supplierHelp: 'Picked from their file where the purchasing module is installed; typed where it is not.',
        supplierPlaceholder: 'Start typing the name…',
        supplierLinked: 'Linked to their supplier file',
        supplierUnlink: 'Unlink from the file',
        code: 'Code', codeHelp: 'What the QR will say. Use whatever is already painted on the aisle.',
        codePlaceholder: 'A-03-2',
        kind: 'Kind', parent: 'Inside', parentHelp: 'A shelf goes inside an aisle, and an aisle inside a building.',
        noParent: 'At the root',
      },
      move: {
        open: 'Move stock',
        from: 'From', to: 'To', pickLocation: 'Pick a location',
        quantity: 'Quantity ({unit})',
        availableHere: 'There are {quantity} there.',
        reason: 'Reason', reasonRequired: 'Reason (required)',
        reasonPlaceholder: 'For the Puigcerdà job, annual count…',
        adjustmentNote: 'An adjustment overrules the system with what you counted. It carries your name.',
        record: 'Record', done: 'Movement recorded',
        kind: { in: 'In', out: 'Out', transfer: 'Transfer', adjustment: 'Adjustment' },
        title: {
          in: 'Record an entry', out: 'Record an issue',
          transfer: 'Transfer between locations', adjustment: 'Correct the count',
        },
      },
      scan: {
        title: 'Scan',
        subtitle: 'Point it at a shelf or at a part',
        aim: 'Frame the code',
        typeLabel: 'Or type the code',
        typePlaceholder: 'A-03-2, MAT-0184…',
        look: 'Look up',
        noReader: 'This browser cannot read codes. Type the code or use a handheld scanner.',
        unknown: 'Nobody knows the code {code}',
        unknownHint: 'It is not a location or a material in this warehouse.',
        inTotal: 'In total there are',
        openCard: 'Open the card',
        recent: 'Recent scans',
      },
      labels: {
        title: 'Print labels',
        size: 'Label size', width: 'Width (mm)', height: 'Height (mm)',
        padding: 'Inner margin (mm)', copies: 'Copies of each',
        custom: 'Custom…',
        tally: '{items} items · {labels} labels · {width} × {height} mm',
        many: 'You are about to print {count} labels. Generating them takes a few seconds; do not close the window.',
        empty: 'Nothing selected',
        andMore: 'and {count} more',
        print: 'Print {count}', printTest: 'Print a test',
        help: {
          summary: 'Not coming out right on the thermal printer?',
          printer: 'In the print dialog, pick the thermal printer, not the ordinary one.',
          paper: 'Paper: the loaded label, or “{width} × {height} mm”.',
          margins: 'Margins: none. Scale: 100 %, not “Fit”.',
          headers: 'Turn off headers and footers.',
          test: 'Print one test label before the whole run.',
        },
      },
      deliveries: {
        title: 'Delivery notes',
        subtitle: 'What came through the door',
        detailTitle: 'Delivery note',
        new: 'New delivery note',
        wizardSubtitle: 'Photograph it and check it over',
        searchPlaceholder: 'Number, supplier…',
        empty: 'No delivery notes yet',
        emptyHint: 'Photograph the first one and it will be read for you.',
        noNumber: 'No number',
        unmatched: '{count} without a material',
        status: { draft: 'To check', confirmed: 'Confirmed', cancelled: 'Discarded' },
        table: { number: 'Note', supplier: 'Supplier', date: 'Date', lines: 'Lines', total: 'Total' },
        intakeLead: 'Take a photograph of the delivery note and it will be read for you.',
        takePhoto: 'Take a photo', takePhotoHint: 'Back camera',
        uploadFile: 'Upload an image', uploadFileHint: 'JPG, PNG or WEBP',
        pdfNote: 'Only photographs can be read for now. If it came as a PDF, open it and photograph the screen.',
        reading: 'Reading the delivery note…',
        typeInstead: 'I would rather type it',
        header: 'Header',
        receivedBy: 'Received by',
        lines: 'Lines',
        unmatchedBanner: '{count} lines still have no material. Those will not enter stock.',
        allMatched: 'Every line has a material. It can be confirmed.',
        material: 'Material', pickMaterial: 'Pick the material',
        unitPrice: 'Unit price',
        destination: 'Where it goes', destinationHelp: 'Left empty, it goes to the default location.',
        defaultLocation: 'Default location',
        addLine: 'Add a line',
        rematch: 'Look for materials again',
        confirm: 'Confirm {count} lines',
        confirmTitle: 'Confirm this delivery note?',
        confirmMessage: '{count} lines will enter stock. It cannot be edited afterwards.',
        confirmWithUnmatched: '{stocked} lines will enter stock. The {skipped} with no material are left out.',
        confirmed: 'Delivery note confirmed: {count} movements',
        alreadyDone: 'That delivery note was already confirmed',
        confirmedOn: 'Confirmed on {at}. The stock has already moved.',
        cancelledBecause: 'Discarded. Reason: {reason}',
        discard: 'Discard', discardTitle: 'Discard this delivery note?',
        discardMessage: 'Nothing will enter stock. It is kept in case anybody asks.',
        truncated: 'It was long and the last lines may be missing. Check them.',
        confidence: { high: 'Clear', medium: 'Doubtful', low: 'Barely legible' },
        matchLayer: {
          reference: 'By reference', alias: 'Learned', similarity: 'By similarity',
          ai: 'Proposed by AI', manual: 'By hand', created: 'Created',
        },
      },
      notifications: {
        belowMinimum: 'Material below its minimum',
        lowStock: {
          title: 'Material below its minimum',
          body: '{material}: {stock} left, minimum {minimum}.',
        },
      },
    },

    errors: {
      DUPLICATE_NAME: 'A category with that name already exists.',
      PARENT_ARCHIVED: 'The place this one hangs from is archived. Bring that one back first.',
      INSUFFICIENT_STOCK: 'There is not that much in that location',
      DUPLICATE_CODE: 'That code is already in use',
      DUPLICATE_SUPPLIER_REF: 'Another material already uses that supplier reference',
      HAS_STOCK: 'It still has stock. Move it out before archiving it.',
      HAS_CHILDREN: 'It still has locations inside it',
      HAS_MATERIALS: 'It still has materials in it',
      IS_DEFAULT: 'It is the default location. Name another one first.',
      ALREADY_CONFIRMED: 'That delivery note is already confirmed',
      NOTHING_TO_STOCK: 'No line has both a material and a quantity',
      CONFIRMING: 'That delivery note is being confirmed right now',
      PARTIALLY_STOCKED: 'Part of this delivery note is already in stock. Confirm it again to finish it.',
      NO_DEFAULT_LOCATION: 'There is no default location',
      UNSUPPORTED_DOCUMENT: 'Only photographs can be read. Photograph the delivery note.',
      UNREADABLE_DOCUMENT: 'It could not be read. Try another photograph.',
      fields: {
        SAME_LOCATION: 'Origin and destination are the same place',
        INSUFFICIENT_STOCK: 'There are only {available}',
        ARCHIVED: 'It is archived',
        NOT_FOUND: 'It does not exist',
        UNSUPPORTED_TYPE: 'That kind of file cannot be read',
      },
    },
    modules: { inventory: 'Warehouse' },
    assistant: {
      openers: {
        inventory: {
          'below-minimum': 'What is below its minimum?',
          'stock-value': 'What is our stock worth?',
          'pending-deliveries': 'Which delivery notes are still to check?',
        },
      },
    },
    permissions: {
      inventory: {
        read: 'See the catalogue and its prices',
        write: 'Add and edit materials',
        move: 'Scan and move stock',
        deliveries: 'Upload and confirm delivery notes',
        locations: 'Maintain the warehouse map',
      },
    },
    settings: {
      ns: {
        inventory: {
          title: 'Warehouse',
          subtitle: 'How this particular warehouse works',
          allowNegativeStock: {
            label: 'Let a location go below zero',
            help: 'Off, an issue larger than what is on the shelf is refused. Turn it on where the delivery note arrives days after the pallet: otherwise the fitter cannot record what they genuinely took.',
          },
          labelSize: {
            label: 'Default label size (mm)',
            help: 'The roll this warehouse has bought. Anyone printing can still change it for one run.',
          },
        },
      },
    },
    audit: {
      actions: {
        inventory: {
          material_created: 'Material added',
          material_updated: 'Material edited',
          material_archived: 'Material archived',
          material_restored: 'Material brought back',
          category_created: 'Family created',
          category_updated: 'Family edited',
          category_archived: 'Family archived',
          category_restored: 'Family brought back',
          location_created: 'Location created',
          location_updated: 'Location edited',
          location_archived: 'Location archived',
          location_restored: 'Location brought back',
          location_default_set: 'Default location changed',
          stock_in: 'Stock in',
          stock_out: 'Stock out',
          stock_transfer: 'Transfer between locations',
          stock_adjustment: 'Count adjustment',
          delivery_created: 'Delivery note opened',
          delivery_updated: 'Delivery note corrected',
          delivery_confirmed: 'Delivery note confirmed into stock',
          delivery_cancelled: 'Delivery note discarded',
        },
      },
    },
  },

  fr: {
    inventory: {
      assistant: {
        move: {
          summary: {
            out: 'Sortir {quantity} {unit} de {material} de {from} : {fromBefore} → {fromAfter} restants',
            in: 'Entrer {quantity} {unit} de {material} dans {to} : {toBefore} → {toAfter}',
            between: 'Déplacer {quantity} {unit} de {material} de {from} vers {to}',
            fallback: 'Enregistrer un mouvement de {quantity}',
          },
          action: {
            out: 'Enregistrer une consommation',
            in: 'Enregistrer une entrée',
            between: 'Déplacer du matériel',
          },
          field: {
            material: 'Article',
            quantity: 'Quantité',
            from: 'Origine',
            to: 'Destination',
            reason: 'Motif',
          },
          value: {
            quantity: '{quantity} {unit}',
          },
          where: 'Emplacement',
          after: {
            out: 'Il restera {fromAfter} {unit} à {from}',
            in: 'Il y aura {toAfter} {unit} à {to}',
            between: 'Il restera {fromAfter} {unit} à {from} et {toAfter} à {to}',
          },
          outcome: {
            done: 'Mouvement enregistré · {stock} au total',
            below: 'Mouvement enregistré · {stock} au total, sous le minimum',
          },
        },
      },
      title: 'Entrepôt',
      dashboard: {
        belowMinimum: 'Sous le minimum',
        belowMinimumHelp: 'Combien de références ont moins de stock que le minimum qui leur est fixé.',
        outOfStock: 'En rupture',
        outOfStockHelp: 'Combien de références sont à zéro ou en négatif, qu\'elles aient un minimum ou non.',
        value: 'Valeur du magasin',
        valueHelp: 'Ce que vaut ce qui est en rayon, au prix de revient, en euros.',
        materials: 'Références',
        materialsHelp: 'Combien de matériaux figurent au catalogue, hors archivés.',
        toBuy: 'À réapprovisionner',
        toBuyHelp: 'Les matériaux sous leur minimum, avec ce qu\'il en reste. Étirez la carte et il en tient plus.',
      },
      saved: 'Enregistré',
      materials: {
        title: 'Matériel',
        subtitle: 'Ce que vous avez, ce qu’il en reste et où il se trouve',
        detailTitle: 'Article',
        new: 'Nouvel article',
        newTitle: 'Nouvel article',
        editTitle: 'Modifier l’article',
        formSubtitle: 'Ce que c’est, ce que ça coûte et d’où ça vient',
        whatItIs: 'Ce que c’est',
        whatItCosts: 'Ce que ça coûte',
        whereItComesFrom: 'D’où ça vient',
        label: 'Étiquette',
        printLabels: 'Imprimer {count} étiquettes',
        empty: {
          none: 'Pas encore d’articles',
          noneHint: 'Créez le premier, ou importez un bon de livraison et créez-les depuis là.',
          filtered: 'Aucun résultat',
          filteredHint: 'Essayez d’enlever un filtre ou de chercher autre chose.',
        },
      },
      material: {
        whereIsIt: 'Où est-il ?',
        nowhere: 'Rien dans aucun emplacement',
        nowhereHint: 'Enregistrez une entrée pour qu’il y ait du stock.',
        details: 'Fiche',
        history: 'Mouvements',
        noHistory: 'Rien n’a encore bougé',
      },
      locations: {
        title: 'Emplacements',
        subtitle: 'Le plan de l’entrepôt : bâtiments, allées, rayonnages, camionnettes et chantiers',
        map: 'Plan',
        new: 'Nouvel emplacement',
        edit: 'Modifier l’emplacement',
        empty: 'Le plan est vide',
        emptyHint: 'Commencez par le bâtiment, puis les allées et les rayonnages.',
        pickOne: 'Choisissez un emplacement',
        pickOneHint: 'Sélectionnez-le sur le plan pour voir ce qu’il contient.',
        nothingHere: 'Rien ici',
        includeChildren: 'Inclure ce qui est en dessous',
        makeDefault: 'En faire l’emplacement par défaut',
        default: 'Par défaut',
        defaultSet: 'C’est désormais l’emplacement par défaut',
        archive: 'Archiver',
        archived: 'Emplacement archivé',
        archiveConfirm: 'Archiver cet emplacement ?',
        archiveConfirmHint: 'Les étiquettes déjà collées dessus ne trouveront plus rien.',
        printLabels: 'Imprimer {count} étiquettes',
      },
      locationKinds: {
        warehouse: 'Bâtiment', zone: 'Allée', shelf: 'Rayonnage',
        bin: 'Casier', vehicle: 'Camionnette', site: 'Chantier',
      },
      units: {
        unit: 'u.', box: 'boîte', pallet: 'palette', set: 'jeu', roll: 'rouleau',
        kg: 'kg', g: 'g', l: 'l', m: 'm', m2: 'm²', m3: 'm³',
      },
      filters: {
        search: 'Rechercher',
        searchPlaceholder: 'Nom, code, référence…',
        category: 'Famille',
        allCategories: 'Toutes',
        location: 'Emplacement',
        anywhere: 'N’importe où',
        state: 'État',
        allStates: 'Tous',
      },
      state: { ok: 'Correct', belowMinimum: 'Sous le minimum', outOfStock: 'En rupture' },
      table: {
        material: 'Article', category: 'Famille', stock: 'Stock', minimum: 'Minimum',
        cost: 'Coût', value: 'Valeur', state: 'État', location: 'Emplacement',
        quantity: 'Quantité', movement: 'Mouvement', route: 'Trajet', after: 'Reste',
        reason: 'Motif', who: 'Qui', when: 'Quand', here: 'Ici', everywhere: 'Au total',
      },
      kpi: {
        materials: 'Articles', value: 'Valeur du stock',
        belowMinimum: 'Sous le minimum', outOfStock: 'En rupture', inStock: 'Il y a',
      },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      fields: {
        name: 'Nom', description: 'Description', category: 'Famille', noCategory: 'Sans famille',
        unit: 'Unité', costPrice: 'Prix de revient', salePrice: 'Prix de vente',
        minStock: 'Minimum', minStockHelp: 'En dessous, l’acheteur est prévenu. 0 = jamais.',
        leadTimeDays: 'Délai (jours)',
        supplierRef: 'Référence fournisseur',
        supplierRefHelp: 'Son code. C’est la première chose cherchée en lisant son bon de livraison.',
        barcode: 'Code-barres', barcodeHelp: 'L’EAN imprimé sur le carton, s’il y en a un.',
        supplier: 'Fournisseur',
        supplierHelp: 'Choisi dans sa fiche si le module achats est installé ; saisi sinon.',
        supplierPlaceholder: 'Commencez à saisir le nom…',
        supplierLinked: 'Lié à sa fiche fournisseur',
        supplierUnlink: 'Détacher de la fiche',
        code: 'Code', codeHelp: 'Ce que dira le QR. Reprenez celui déjà peint sur l’allée.',
        codePlaceholder: 'A-03-2',
        kind: 'Type', parent: 'À l’intérieur de', parentHelp: 'Le rayonnage est dans l’allée, et l’allée dans le bâtiment.',
        noParent: 'À la racine',
      },
      move: {
        open: 'Déplacer du stock',
        from: 'Depuis', to: 'Vers', pickLocation: 'Choisissez un emplacement',
        quantity: 'Quantité ({unit})',
        availableHere: 'Il y en a {quantity} là-bas.',
        reason: 'Motif', reasonRequired: 'Motif (obligatoire)',
        reasonPlaceholder: 'Pour le chantier de Puigcerdà, inventaire annuel…',
        adjustmentNote: 'Un ajustement corrige le système avec ce que vous avez compté. Il porte votre nom.',
        record: 'Enregistrer', done: 'Mouvement enregistré',
        kind: { in: 'Entrée', out: 'Sortie', transfer: 'Transfert', adjustment: 'Ajustement' },
        title: {
          in: 'Enregistrer une entrée', out: 'Enregistrer une sortie',
          transfer: 'Transférer entre emplacements', adjustment: 'Corriger le comptage',
        },
      },
      scan: {
        title: 'Scanner',
        subtitle: 'Visez un rayonnage ou un article',
        aim: 'Cadrez le code',
        typeLabel: 'Ou saisissez le code',
        typePlaceholder: 'A-03-2, MAT-0184…',
        look: 'Chercher',
        noReader: 'Ce navigateur ne sait pas lire les codes. Saisissez le code ou utilisez une douchette.',
        unknown: 'Personne ne connaît le code {code}',
        unknownHint: 'Ce n’est ni un emplacement ni un article de cet entrepôt.',
        inTotal: 'Au total il y a',
        openCard: 'Ouvrir la fiche',
        recent: 'Derniers scans',
      },
      labels: {
        title: 'Imprimer des étiquettes',
        size: 'Format d’étiquette', width: 'Largeur (mm)', height: 'Hauteur (mm)',
        padding: 'Marge intérieure (mm)', copies: 'Copies de chacune',
        custom: 'Personnalisé…',
        tally: '{items} éléments · {labels} étiquettes · {width} × {height} mm',
        many: 'Vous allez imprimer {count} étiquettes. Les générer prend quelques secondes ; ne fermez pas la fenêtre.',
        empty: 'Rien de sélectionné',
        andMore: 'et {count} de plus',
        print: 'Imprimer {count}', printTest: 'Imprimer un test',
        help: {
          summary: 'Le rendu est mauvais sur l’imprimante thermique ?',
          printer: 'Dans la boîte d’impression, choisissez l’imprimante thermique, pas l’ordinaire.',
          paper: 'Papier : celui de l’étiquette chargée, ou « {width} × {height} mm ».',
          margins: 'Marges : aucune. Échelle : 100 %, pas « Ajuster ».',
          headers: 'Désactivez en-têtes et pieds de page.',
          test: 'Imprimez un test avant toute la série.',
        },
      },
      deliveries: {
        title: 'Bons de livraison',
        subtitle: 'Ce qui est entré par la porte',
        detailTitle: 'Bon de livraison',
        new: 'Nouveau bon',
        wizardSubtitle: 'Photographiez-le et vérifiez-le',
        searchPlaceholder: 'Numéro, fournisseur…',
        empty: 'Pas encore de bons de livraison',
        emptyHint: 'Photographiez le premier et il sera lu pour vous.',
        noNumber: 'Sans numéro',
        unmatched: '{count} sans article',
        status: { draft: 'À vérifier', confirmed: 'Confirmé', cancelled: 'Abandonné' },
        table: { number: 'Bon', supplier: 'Fournisseur', date: 'Date', lines: 'Lignes', total: 'Total' },
        intakeLead: 'Photographiez le bon de livraison et il sera lu pour vous.',
        takePhoto: 'Prendre une photo', takePhotoHint: 'Caméra arrière',
        uploadFile: 'Importer une image', uploadFileHint: 'JPG, PNG ou WEBP',
        pdfNote: 'Seules les photos sont lues pour l’instant. Si c’est un PDF, ouvrez-le et photographiez l’écran.',
        reading: 'Lecture du bon…',
        typeInstead: 'Je préfère le saisir',
        header: 'En-tête',
        receivedBy: 'Reçu par',
        lines: 'Lignes',
        unmatchedBanner: 'Il reste {count} lignes sans article. Elles n’entreront pas en stock.',
        allMatched: 'Chaque ligne a un article. On peut confirmer.',
        material: 'Article', pickMaterial: 'Choisissez l’article',
        unitPrice: 'Prix unitaire',
        destination: 'Destination', destinationHelp: 'Laissé vide, cela part à l’emplacement par défaut.',
        defaultLocation: 'Emplacement par défaut',
        addLine: 'Ajouter une ligne',
        rematch: 'Rechercher à nouveau',
        confirm: 'Confirmer {count} lignes',
        confirmTitle: 'Confirmer ce bon de livraison ?',
        confirmMessage: '{count} lignes vont entrer en stock. Ensuite, plus de modification possible.',
        confirmWithUnmatched: '{stocked} lignes entreront. Les {skipped} sans article restent dehors.',
        confirmed: 'Bon confirmé : {count} mouvements',
        alreadyDone: 'Ce bon était déjà confirmé',
        confirmedOn: 'Confirmé le {at}. Le stock a déjà bougé.',
        cancelledBecause: 'Abandonné. Motif : {reason}',
        discard: 'Abandonner', discardTitle: 'Abandonner ce bon de livraison ?',
        discardMessage: 'Rien n’entrera en stock. Il reste conservé au cas où.',
        truncated: 'Il était long et les dernières lignes peuvent manquer. Vérifiez-les.',
        confidence: { high: 'Bien lisible', medium: 'Douteux', low: 'Presque illisible' },
        matchLayer: {
          reference: 'Par référence', alias: 'Appris', similarity: 'Par ressemblance',
          ai: 'Proposé par l’IA', manual: 'À la main', created: 'Créé',
        },
      },
      notifications: {
        belowMinimum: 'Article sous son minimum',
        lowStock: {
          title: 'Article sous son minimum',
          body: '{material} : il en reste {stock}, le minimum est {minimum}.',
        },
      },
    },

    errors: {
      DUPLICATE_NAME: 'Une catégorie porte déjà ce nom.',
      PARENT_ARCHIVED: 'L’emplacement dont celui-ci dépend est archivé. Restaurez-le d’abord.',
      INSUFFICIENT_STOCK: "Il n'y en a pas autant à cet emplacement",
      DUPLICATE_CODE: 'Ce code est déjà utilisé',
      DUPLICATE_SUPPLIER_REF: 'Un autre article utilise déjà cette référence fournisseur',
      HAS_STOCK: "Il reste du stock. Sortez-le avant d'archiver.",
      HAS_CHILDREN: 'Il contient encore des emplacements',
      HAS_MATERIALS: 'Elle contient encore des articles',
      IS_DEFAULT: "C'est l'emplacement par défaut. Désignez-en un autre d'abord.",
      ALREADY_CONFIRMED: 'Ce bon est déjà confirmé',
      NOTHING_TO_STOCK: "Aucune ligne n'a à la fois un article et une quantité",
      CONFIRMING: 'Ce bon est en cours de confirmation',
      PARTIALLY_STOCKED: 'Une partie de ce bon est déjà en stock. Confirmez-le à nouveau pour le terminer.',
      NO_DEFAULT_LOCATION: "Il n'y a pas d'emplacement par défaut",
      UNSUPPORTED_DOCUMENT: 'Seules les photos sont lues. Photographiez le bon.',
      UNREADABLE_DOCUMENT: 'Lecture impossible. Essayez une autre photo.',
      fields: {
        SAME_LOCATION: "L'origine et la destination sont le même endroit",
        INSUFFICIENT_STOCK: "Il n'y en a que {available}",
        ARCHIVED: "C'est archivé",
        NOT_FOUND: "N'existe pas",
        UNSUPPORTED_TYPE: 'Ce type de fichier ne peut pas être lu',
      },
    },
    modules: { inventory: 'Entrepôt' },
    assistant: {
      openers: {
        inventory: {
          'below-minimum': 'Qu’est-ce qui est sous son minimum ?',
          'stock-value': 'Combien vaut notre stock ?',
          'pending-deliveries': 'Quels bons de livraison restent à vérifier ?',
        },
      },
    },
    permissions: {
      inventory: {
        read: 'Voir le catalogue et ses prix',
        write: 'Créer et modifier des articles',
        move: 'Scanner et déplacer du stock',
        deliveries: 'Importer et confirmer des bons de livraison',
        locations: 'Tenir à jour le plan de l’entrepôt',
      },
    },
    settings: {
      ns: {
        inventory: {
          title: 'Entrepôt',
          subtitle: 'Comment fonctionne cet entrepôt en particulier',
          allowNegativeStock: {
            label: 'Laisser un emplacement passer sous zéro',
            help: 'Désactivé, une sortie supérieure au stock est refusée. Activez-le là où le bon arrive des jours après la palette : sinon le monteur ne peut pas noter ce qu’il a réellement pris.',
          },
          labelSize: {
            label: 'Format d’étiquette par défaut (mm)',
            help: 'Le rouleau acheté par cet entrepôt. Qui imprime peut le changer pour une série.',
          },
        },
      },
    },
    audit: {
      actions: {
        inventory: {
          material_created: 'Article créé',
          material_updated: 'Article modifié',
          material_archived: 'Article archivé',
          material_restored: 'Article remis au catalogue',
          category_created: 'Famille créée',
          category_updated: 'Famille modifiée',
          category_archived: 'Famille archivée',
          category_restored: 'Famille remise au catalogue',
          location_created: 'Emplacement créé',
          location_updated: 'Emplacement modifié',
          location_archived: 'Emplacement archivé',
          location_restored: 'Emplacement remis au dépôt',
          location_default_set: 'Emplacement par défaut changé',
          stock_in: 'Entrée de stock',
          stock_out: 'Sortie de stock',
          stock_transfer: 'Transfert entre emplacements',
          stock_adjustment: 'Ajustement de comptage',
          delivery_created: 'Bon de livraison ouvert',
          delivery_updated: 'Bon de livraison corrigé',
          delivery_confirmed: 'Bon de livraison confirmé en stock',
          delivery_cancelled: 'Bon de livraison abandonné',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'inventory')))

export default { routes, navigation, messages }
